import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { EmailQuoteDetails } from '@packages/types';
import { abandonedCartSchema } from '@packages/utils';

import { Button } from '@elements/button';
import { Input } from '@elements/input';
import { Notification } from '@elements/notification';
import { TextWithMd } from '@elements/text/textWithMd';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEmailQuote, useEmailQuoteModalCSNumberExperiment } from '@hooks';
import { i18n } from '@i18n';
import { EMAIL_QUOTE_FLOW_TYPE } from '@types';
import { testProps } from '@utils';

type Props = {
  flow?: EMAIL_QUOTE_FLOW_TYPE;
};

export const EmailQuote: React.FC<Props> = ({ flow = EMAIL_QUOTE_FLOW_TYPE.FOOTER }) => {
  const { sendEmailQuote, closeEmailQuoteModal, hasExceedEmailLimit, isEmailQuoteSubmitting } =
    useEmailQuote();
  const { payload } = useEmailQuoteModalCSNumberExperiment();
  const description = i18n.t(
    payload?.csNumberEnabled || flow === EMAIL_QUOTE_FLOW_TYPE.FOOTER
      ? 'emailQuote.sendEmail.description.customerService'
      : 'emailQuote.sendEmail.description.default',
  );
  const firstNameValidationMessage = i18n.t('emailQuote.sendEmail.inputs.firstName.validation');
  const lastNameValidationMessage = i18n.t('emailQuote.sendEmail.inputs.lastName.validation');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<EmailQuoteDetails>({
    resolver: zodResolver(
      abandonedCartSchema(firstNameValidationMessage, lastNameValidationMessage),
    ),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const onSubmit = async (data: EmailQuoteDetails) => {
    await sendEmailQuote(data);
  };

  useEffect(() => {
    if (hasExceedEmailLimit) {
      reset();
    }
  }, [hasExceedEmailLimit, reset]);

  useEffect(() => {
    (window as any).Still_deciding = true;
    return () => {
      (window as any).Still_deciding = false;
    };
  }, []);

  return (
    <form className="w-full relative md:px-4">
      <TextWithMd
        variant="body-16/r"
        className="pb-6 text-left whitespace-pre-wrap"
        {...testProps('text-email-quote-description')}>
        {description?.replace(/\n/gi, '&nbsp;\n')}
      </TextWithMd>
      {hasExceedEmailLimit && (
        <Notification
          variant="error"
          header={i18n.t('emailQuote.sendEmail.exceededLimitNotification.title')}
          body={i18n.t('emailQuote.sendEmail.exceededLimitNotification.description')}
          className="mb-s12"
        />
      )}
      <div className="flex-col mb-4 space-y-4">
        <Input
          placeholder={i18n.t('emailQuote.sendEmail.inputs.firstName.placeholder')}
          {...register('firstName')}
          autoComplete="given-name"
          errorMessage={errors.firstName?.message}
          disabled={hasExceedEmailLimit}
          {...testProps('input-first-name')}
        />
        <Input
          placeholder={i18n.t('emailQuote.sendEmail.inputs.lastName.placeholder')}
          {...register('lastName')}
          autoComplete="family-name"
          errorMessage={errors.lastName?.message}
          disabled={hasExceedEmailLimit}
          {...testProps('input-last-name')}
        />
        <Input
          type="email"
          placeholder={i18n.t('emailQuote.sendEmail.inputs.email.placeholder')}
          {...register('email')}
          autoComplete="email"
          errorMessage={errors.email?.message}
          disabled={hasExceedEmailLimit}
          {...testProps('input-email')}
        />
        <TextWithMd
          variant="footnote-12/r"
          className="text-left"
          {...testProps('text-email-quote-agreement')}>
          {i18n.t('emailQuote.sendEmail.agreement')}
        </TextWithMd>
      </div>
      <div className={'flex flex-col space-y-3'}>
        <Button
          variant="primary"
          type="submit"
          onClick={handleSubmit(onSubmit)}
          disabled={hasExceedEmailLimit}
          title={i18n.t('emailQuote.sendEmail.buttonActions.send')}
          isLoading={isEmailQuoteSubmitting}
          {...testProps('btn-send-email')}
        />
        <Button
          variant="secondary"
          type="submit"
          onClick={closeEmailQuoteModal}
          title={i18n.t('emailQuote.sendEmail.buttonActions.noThanks')}
          {...testProps('btn-cancel')}
        />
      </div>
    </form>
  );
};
